import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProfileProvider } from "@/context/ProfileContext";
import { ConsentProvider } from "@/context/ConsentContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import AIConsultant from "@/components/AIConsultant";

import Landing from "@/pages/Landing";
import Quiz from "@/pages/Quiz";
import Results from "@/pages/Results";
import Armocromia from "@/pages/Armocromia";
import Catalog from "@/pages/Catalog";
import Outfits from "@/pages/Outfits";
import Hair from "@/pages/Hair";
import SeoPage from "@/pages/SeoPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import GuideIndex from "@/pages/GuideIndex";
import Legal from "@/pages/Legal";
import Studio from "@/pages/Studio";
import ScrollToTop from "@/components/ScrollToTop";

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-charcoal/50">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Shell><Landing /></Shell>} />
      <Route path="/quiz" element={<Shell><Quiz /></Shell>} />
      <Route path="/results" element={<Shell><Results /></Shell>} />
      <Route path="/studio/:topic" element={<Shell><Studio /></Shell>} />
      <Route path="/armocromia" element={<Shell><Armocromia /></Shell>} />
      <Route path="/catalog/:domain" element={<Shell><Catalog /></Shell>} />
      <Route path="/outfits" element={<Shell><Outfits /></Shell>} />
      <Route path="/hair" element={<Shell><Hair /></Shell>} />
      <Route path="/guide" element={<Shell><GuideIndex /></Shell>} />
      <Route path="/guide/:slug" element={<Shell><SeoPage /></Shell>} />
      <Route path="/cookie-policy" element={<Shell><Legal type="cookie" /></Shell>} />
      <Route path="/privacy-policy" element={<Shell><Legal type="privacy" /></Shell>} />
      <Route path="/termini-condizioni" element={<Shell><Legal type="terms" /></Shell>} />
      <Route path="/disclaimer" element={<Shell><Legal type="disclaimer" /></Shell>} />
      <Route path="/login" element={<Shell><Login /></Shell>} />
      <Route path="/register" element={<Shell><Register /></Shell>} />
      <Route path="/dashboard" element={<Protected><Shell><Dashboard /></Shell></Protected>} />
      <Route path="/admin" element={<Protected admin><Shell><Admin /></Shell></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <ProfileProvider>
          <ConsentProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Toaster position="top-center" richColors />
              <AppRouter />
              <CookieBanner />
              <AIConsultant />
            </BrowserRouter>
          </ConsentProvider>
        </ProfileProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
